import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { MeshBVH, acceleratedRaycast, computeBoundsTree, disposeBoundsTree } from 'three-mesh-bvh';
import { useShallow } from 'zustand/shallow';
import { useEditorStore } from '../../../stores';
import useTrimlineStore from '../../../stores/useTrimlineStore';
import { CreateElongationPreviewMaterial } from '../../../utils/PreviewMaterialsHandler';
import { generateCurvePoints, getSurfaceNormalAtPoint,
  projectCurvePointsOntoMesh, projectPointOntoMesh,
  convertToWorldPositions } from './trimlineHelpers';
import { cutMeshByLoop, cutMeshByLoopBothParts, isPointBelowTrimline } from './curveSlice';
import {
  cutMeshByLoopAsync,
  reconstructGeometryFromWorkerResult,
  prepareMeshDataForWorker
} from './TrimlineWorkerIntegration';

import { useToolManager } from '../../../hooks/useToolManager';
import { TOOLS_CONFIG } from '../../../constants/tools';

// Constants for visual styling - made more visible
const POINT_COLORS = {
  default: '#4A90E2',
  selected: '#FF6B6B',
  hover: '#FFD93D'
};
const POINT_SIZE = 0.25; // Increased size for better visibility
const HANDLE_COLOR = '#FFD93D'; //bright yellow
const HANDLE_SIZE = 0.25; // Increased size for better visibility
const HANDLE_LINE_COLOR = '#FFD93D';
const CURVE_COLOR = '#10b981';
const LINE2_WIDTH = 6.0; // Increased width for better visibility
const TANGENT_COLOR = '#22d3ee'; // cyan
const NORMAL_COLOR = '#f472b6'; // pink
const DEBUG_VECTOR_LENGTH = 1.2; // world units for debug lines

//Render Order
const RENDER_ORDER_POINTS = 1000;
const RENDER_ORDER_CURVE = 1e6; // Higher render order to ensure curve renders on top
const RENDER_ORDER_HANDLES = 1002;

const TrimlineHandler = () => {
  const [scene, shadedMesh, material, controls, camera, renderer, replaceShadedMesh, ghostModeEnabled] = useEditorStore(
    useShallow((s) => [
      s.sceneRefs.scene,
      s.sceneRefs.shadedMesh,
      s.sceneRefs.material,
      s.sceneRefs.controls,
      s.sceneRefs.camera,
      s.sceneRefs.renderer,
      s.replaceShadedMesh,
      s.ghostModeEnabled,
    ])
  );

  const [trimlineState, previewMesh, setPreviewMesh, selectPoint, updatePoint, updateBezierHandle, isTrimlineApplied, isAppliedFromDraft, setOriginalMesh, setUpperMesh, setInitialPoints, shouldReinitialize, clearReinitializeFlag] = useTrimlineStore(
    useShallow((s) => [
      s.trimline,
      s.previewMesh,
      s.setPreviewMesh,
      s.selectPoint,
      s.updatePoint,
      s.updateBezierHandle,
      s.isTrimlineApplied,
      s.isAppliedFromDraft,
      s.setOriginalMesh,
      s.setUpperMesh,
      s.setInitialPoints,
      s.shouldReinitialize,
      s.clearReinitializeFlag
    ])
  );

  const { run } = useToolManager();

  // Refs for managing Three.js objects
  const pointGroupRef = useRef(null);
  const curveGroupRef = useRef(null);
  const handlesGroupRef = useRef(null);
  const previewMeshRef = useRef(null);
  const originalMaterialRef = useRef(null);
  const baseGeomRef = useRef(null);
  const lastMeshRef = useRef(null);
  const bvhRef = useRef(null);
  const debugGroupRef = useRef(null);
  const pointNormalsRef = useRef(new Map()); // Cache for point normals

  // Operation tracking for async operations
  const abortControllerRef = useRef(null); // For cancelling in-flight operations
  const operationIdRef = useRef(0);        // Operation ID to prevent race conditions
  
  // Track curve state hash for recomputation detection
  const previewCurveStateHashRef = useRef(null); // Hash of points+handles used to generate current preview
  
  // Raycasting for point selection
  const raycaster = useRef(new THREE.Raycaster());
  const mouse = useRef(new THREE.Vector2());
  const isDragging = useRef(false);
  const dragStart = useRef(new THREE.Vector3());
  const draggingHandleRef = useRef(null);
  const draggingPointRef = useRef(false);
  const handleDragPlaneRef = useRef(new THREE.Plane());
  const handleAnchorRef = useRef(new THREE.Vector3());
  const cameraDirRef = useRef(new THREE.Vector3());
  const tmpIntersectionRef = useRef(new THREE.Vector3());


  // Initialize default curve points based on mesh bounds and project onto surface
  const initializeDefaultPoints = (mesh, bvh, scene, debugGroupRef) => {
    if (!mesh) return trimlineState.points;
    
    if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox();
    const bbox = mesh.geometry.boundingBox.clone();
    bbox.applyMatrix4(mesh.matrixWorld);
    
    const center = bbox.getCenter(new THREE.Vector3());
    const size = bbox.getSize(new THREE.Vector3());

    
    // Use a larger radius to ensure the initial raycasting points are outside the mesh
    const radius = Math.max(size.x, size.z) * 0.8;
    const baseHeight = center.y;

    const yOffset = 1;
    const yPositions = [ 0, yOffset, yOffset, 0, yOffset, yOffset]
    
    const defaultPoints = [];
    const numPoints = 6;

    // Calculate points in a guaranteed circular order using angles
    for (let i = 0; i < numPoints; i++) {
      // Start angle adjusted to place the first point at the front (positive Z),
      // and subsequent points are placed clockwise.
      const angle = (Math.PI / 2) - (i / numPoints) * 2 * Math.PI;
      
      const x = center.x + radius * Math.cos(angle);
      const z = center.z + radius * Math.sin(angle);
      
      // We set a slightly higher Y to raycast downwards, which is more reliable
      const y = baseHeight + yPositions[i];// baseHeight + size.y / 2;

      defaultPoints.push({ x, y, z });
    }

    // For debugging: visualize the points before projection
    if (scene && debugGroupRef) {
      if (!debugGroupRef.current) {
        const group = new THREE.Group();
        group.name = 'TrimlineDebugPoints';
        scene.add(group);
        debugGroupRef.current = group;
      }
      debugGroupRef.current.clear(); // Clear previous debug points

      defaultPoints.forEach(point => {
        const geometry = new THREE.SphereGeometry(POINT_SIZE * 0.8, 16, 12);
        const material = new THREE.MeshBasicMaterial({ color: 'red', transparent: true, opacity: 0.7 });
        const sphere = new THREE.Mesh(geometry, material);
        sphere.position.set(point.x, point.y, point.z);
        debugGroupRef.current.add(sphere);
      });
    }

    // Project all ordered points onto the mesh surface and calculate their normals
    // The primary raycasting direction should be towards the mesh center
    const projectedPoints = defaultPoints.map(point => projectPointOntoMesh(point, center, bvh, mesh));
    
    // Calculate and cache surface normals for each point
    projectedPoints.forEach((point, index) => {
      const normal = getSurfaceNormalAtPoint(point, mesh, bvh);
      pointNormalsRef.current.set(index, normal);
    });
    
    return projectedPoints;
  };

  // Create point spheres
  const createPointSphere = (position, index, isSelected = false) => {
    const geometry = new THREE.SphereGeometry(POINT_SIZE, 16, 12);
    const material = new THREE.MeshBasicMaterial({ 
      color: isSelected ? POINT_COLORS.selected : POINT_COLORS.default,
      transparent: false,
      opacity: 0.6
    });
    const sphere = new THREE.Mesh(geometry, material);
    sphere.position.set(position.x, position.y, position.z);
    sphere.userData = { pointIndex: index, isControlPoint: true };
    sphere.name = `TrimlinePoint_${index}`;
    sphere.renderOrder = RENDER_ORDER_POINTS;
    return sphere;
  };

  // Initialize handles in the surface normal plane
  const initializeHandlesInNormalPlane = (pointIndex, handleLength = 2.0, pointsArray = null, alignToWorldXZ = false) => {
    // Use provided points array or fall back to state
    const points = pointsArray || trimlineState.points;
    const point = points[pointIndex];
    if (!point) return null;
    
    const normal = pointNormalsRef.current.get(pointIndex);
    if (!normal) return null;
    
    try {
      // If requested, align handles along the intersection line between the world XZ plane
      // (normal = (0,1,0)) and the point's tangent plane (perpendicular to the surface normal).
      // This guarantees the handles are parallel to XZ while respecting the mesh normal.
      if (alignToWorldXZ) {
        const worldUp = new THREE.Vector3(0, 1, 0);
        const dir = new THREE.Vector3().crossVectors(worldUp, normal).normalize();
        // If the surface normal is parallel to worldUp, the cross is near zero; fallback to X axis
        if (dir.length() < 1e-6) {
          dir.set(1, 0, 0);
        }
        return {
          in: {
            x: -dir.x * handleLength,
            y: -dir.y * handleLength,
            z: -dir.z * handleLength
          },
          out: {
            x: dir.x * handleLength,
            y: dir.y * handleLength,
            z: dir.z * handleLength
          }
        };
      }
      // Create two orthogonal vectors in the plane perpendicular to the normal
      const tempVec = Math.abs(normal.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
      const tangent1 = new THREE.Vector3().crossVectors(normal, tempVec).normalize();
      const tangent2 = new THREE.Vector3().crossVectors(normal, tangent1).normalize();
      
      // For better distribution, use a combination of both tangent vectors
      // This creates handles that are tangent to a circle in the normal plane
      const prevIndex = pointIndex === 0 ? points.length - 1 : pointIndex - 1;
      const nextIndex = (pointIndex + 1) % points.length;
      
      const prevPoint = points[prevIndex];
      const nextPoint = points[nextIndex];
      
      if (prevPoint && nextPoint) {
        // Calculate approximate tangent direction from neighboring points
        const toPrev = new THREE.Vector3(prevPoint.x - point.x, prevPoint.y - point.y, prevPoint.z - point.z);
        const toNext = new THREE.Vector3(nextPoint.x - point.x, nextPoint.y - point.y, nextPoint.z - point.z);
        
        // Project these directions onto the normal plane
        const toPrevProjected = toPrev.clone().sub(normal.clone().multiplyScalar(toPrev.dot(normal))).normalize();
        const toNextProjected = toNext.clone().sub(normal.clone().multiplyScalar(toNext.dot(normal))).normalize();
        
        // Average the directions for a smooth tangent
        const avgTangent = toPrevProjected.clone().add(toNextProjected).normalize();
        
        if (avgTangent.length() > 0.1) { // Valid tangent
          return {
            in: {
              x: -avgTangent.x * handleLength,
              y: -avgTangent.y * handleLength, 
              z: -avgTangent.z * handleLength
            },
            out: {
              x: avgTangent.x * handleLength,
              y: avgTangent.y * handleLength,
              z: avgTangent.z * handleLength
            }
          };
        }
      }
      
      // Fallback: use the primary tangent direction in the normal plane
      return {
        in: {
          x: -tangent1.x * handleLength,
          y: -tangent1.y * handleLength,
          z: -tangent1.z * handleLength
        },
        out: {
          x: tangent1.x * handleLength,
          y: tangent1.y * handleLength,
          z: tangent1.z * handleLength
        }
      };
      
    } catch (error) {
      // Default fallback handles
      return {
        in: { x: -handleLength, y: 0, z: 0 },
        out: { x: handleLength, y: 0, z: 0 }
      };
    }
  };

  // Create bezier handle spheres
  const createHandleSphere = (position, pointIndex, handleType) => {
    const geometry = new THREE.SphereGeometry(HANDLE_SIZE, 8, 6);
    const material = new THREE.MeshBasicMaterial({ 
      color: HANDLE_COLOR,
      transparent: true,
      opacity: 0.6,
      depthTest: false
    });
    const sphere = new THREE.Mesh(geometry, material);
    sphere.position.copy(position);
    sphere.userData = { pointIndex, handleType, isHandle: true };
    sphere.name = `TrimlineHandle_${pointIndex}_${handleType}`;
    sphere.renderOrder = RENDER_ORDER_HANDLES;
    return sphere;
  };

  // Compute per-sample curve normals (T x N_surface) for each projected point
  const computeCurveNormalsFlat = (flatPositions) => {
    const count = (flatPositions?.length || 0) / 3;
    if (!count) return null;

    const normals = new Float32Array(count * 3);
    const pPrev = new THREE.Vector3();
    const pCur = new THREE.Vector3();
    const pNext = new THREE.Vector3();
    const tangent = new THREE.Vector3();
    const surfNormal = new THREE.Vector3();
    const rotatedNormal = new THREE.Vector3();

    for (let i = 0; i < count; i++) {
      const iPrev = (i - 1 + count) % count;
      const iNext = (i + 1) % count;
      const idxPrev = iPrev * 3;
      const idxCur = i * 3;
      const idxNext = iNext * 3;

      pPrev.set(flatPositions[idxPrev], flatPositions[idxPrev + 1], flatPositions[idxPrev + 2]);
      pCur.set(flatPositions[idxCur], flatPositions[idxCur + 1], flatPositions[idxCur + 2]);
      pNext.set(flatPositions[idxNext], flatPositions[idxNext + 1], flatPositions[idxNext + 2]);

      tangent.subVectors(pNext, pPrev).normalize();

      if (shadedMesh && bvhRef.current) {
        const n = getSurfaceNormalAtPoint({ x: pCur.x, y: pCur.y, z: pCur.z }, shadedMesh, bvhRef.current);
        surfNormal.copy(n).normalize();
      } else {
        surfNormal.set(0, 1, 0);
      }

      // Normal in surface plane, perpendicular to tangent: T x N_surface
      rotatedNormal.crossVectors(tangent, surfNormal).normalize();

      normals[idxCur] = rotatedNormal.x;
      normals[idxCur + 1] = rotatedNormal.y;
      normals[idxCur + 2] = rotatedNormal.z;
    }

    return normals;
  };

  // Create curve line and return positions used (projected)
  const createCurve = () => {
    // Use much higher resolution for smoother curves, but still optimize for dragging
    const dynamicResolution = isDragging.current ? 100 : 200; // Increased from 128/512 to 1000/5000
    const baseCurvePositions = generateCurvePoints(
      trimlineState.points,
      trimlineState.bezierHandles,
      dynamicResolution
    );

    const geometry = new LineGeometry();

    let projectedPositions = baseCurvePositions;
    if (shadedMesh && bvhRef.current) {
      projectedPositions = projectCurvePointsOntoMesh(baseCurvePositions, shadedMesh, bvhRef.current);
      
      // Offset curve points above the mesh surface to prevent z-fighting
      const offsetAmount = 0.05; // Adjust this value as needed
      for (let i = 0; i < projectedPositions.length; i += 3) {
        const point = new THREE.Vector3(projectedPositions[i], projectedPositions[i + 1], projectedPositions[i + 2]);
        const normal = getSurfaceNormalAtPoint(
          { x: point.x, y: point.y, z: point.z }, 
          shadedMesh, 
          bvhRef.current
        );
        
        // Offset along surface normal
        projectedPositions[i] += normal.x * offsetAmount;
        projectedPositions[i + 1] += normal.y * offsetAmount;
        projectedPositions[i + 2] += normal.z * offsetAmount;
      }
    }

    geometry.setPositions(projectedPositions);
    
    const material = new LineMaterial({
      color: CURVE_COLOR,
      linewidth: LINE2_WIDTH,
      transparent: true,
      opacity: 1,
      depthWrite: false,
      depthTest: true,
      polygonOffset: true,
      polygonOffsetFactor: -5.0,
      polygonOffsetUnits: -5.0,
    });
    
    const el = renderer?.domElement;
    if (el) material.resolution.set(el.clientWidth, el.clientHeight);
    
    const curve = new Line2(geometry, material);
    curve.computeLineDistances();
    curve.name = 'TrimlineCurve';
    curve.renderOrder = RENDER_ORDER_CURVE;
    curve.frustumCulled = false;
    
    // Attach positions for downstream use
    curve.userData = curve.userData || {};
    curve.userData.positions = projectedPositions;
    
    // Only compute normals if preview is enabled (for performance optimization during dragging)
    if (trimlineState?.previewVisible) {
      try {
        const normals = computeCurveNormalsFlat(projectedPositions);
        if (normals) curve.userData.normals = normals;
      } catch (e) {
      }
    }
    return curve;
  };

  // Ensure groups exist
  const ensureGroups = () => {
    if (!scene) return;
    
    if (!pointGroupRef.current) {
      const group = new THREE.Group();
      group.name = 'TrimlinePointGroup';
      scene.add(group);
      pointGroupRef.current = group;
    }
    
    if (!curveGroupRef.current) {
      const group = new THREE.Group();
      group.name = 'TrimlineCurveGroup';
      scene.add(group);
      curveGroupRef.current = group;
    }
    
    if (!handlesGroupRef.current) {
      const group = new THREE.Group();
      group.name = 'TrimlineHandlesGroup';
      scene.add(group);
      handlesGroupRef.current = group;
    }
  };

  // Dispose groups
  const disposeGroups = () => {
    [pointGroupRef.current, curveGroupRef.current, handlesGroupRef.current, debugGroupRef.current].forEach(group => {
      if (!group) return;
      group.traverse((obj) => {
        obj.geometry?.dispose?.();
        obj.material?.dispose?.();
      });
      group.parent?.remove(group);
    });
    pointGroupRef.current = null;
    curveGroupRef.current = null;
    handlesGroupRef.current = null;
    debugGroupRef.current = null;
  };

  // Build debug line segments for tangents and 90°-rotated normals along the curve
  const addCurveDebugVectors = (flatPositions, flatNormals) => {
    if (!flatPositions || flatPositions.length < 6) return; // need at least 2 points

    const count = flatPositions.length / 3;
    const sampleStep = Math.max(1, Math.floor(count / 150)); // target ~150 samples max

    const tangentLines = [];
    const normalLines = [];

    const tmpPPrev = new THREE.Vector3();
    const tmpPNext = new THREE.Vector3();
    const tmpPCur = new THREE.Vector3();
    const tangent = new THREE.Vector3();
    const surfNormal = new THREE.Vector3();
    const rotatedNormal = new THREE.Vector3();

    for (let i = 0; i < count; i += sampleStep) {
      const iPrev = (i - sampleStep + count) % count;
      const iNext = (i + sampleStep) % count;

      const idxPrev = iPrev * 3;
      const idxCur = i * 3;
      const idxNext = iNext * 3;

      tmpPPrev.set(flatPositions[idxPrev], flatPositions[idxPrev + 1], flatPositions[idxPrev + 2]);
      tmpPCur.set(flatPositions[idxCur], flatPositions[idxCur + 1], flatPositions[idxCur + 2]);
      tmpPNext.set(flatPositions[idxNext], flatPositions[idxNext + 1], flatPositions[idxNext + 2]);

      tangent.subVectors(tmpPNext, tmpPPrev).normalize();

      // Estimate surface normal at this point (if BVH available)
      if (shadedMesh && bvhRef.current) {
        const n = getSurfaceNormalAtPoint({ x: tmpPCur.x, y: tmpPCur.y, z: tmpPCur.z }, shadedMesh, bvhRef.current);
        surfNormal.copy(n).normalize();
      } else {
        // Fallback: use world up as pseudo surface normal
        surfNormal.set(0, 1, 0);
      }

      // Use provided normals if available, else compute via T x N_surface
      if (flatNormals && flatNormals.length === flatPositions.length) {
        rotatedNormal.set(flatNormals[idxCur], flatNormals[idxCur + 1], flatNormals[idxCur + 2]).normalize();
      } else {
        rotatedNormal.crossVectors(tangent, surfNormal).normalize();
      }

      // Tangent segment
      tangentLines.push(
        tmpPCur.x, tmpPCur.y, tmpPCur.z,
        tmpPCur.x + tangent.x * DEBUG_VECTOR_LENGTH,
        tmpPCur.y + tangent.y * DEBUG_VECTOR_LENGTH,
        tmpPCur.z + tangent.z * DEBUG_VECTOR_LENGTH
      );

      // Normal segment
      normalLines.push(
        tmpPCur.x, tmpPCur.y, tmpPCur.z,
        tmpPCur.x + rotatedNormal.x * DEBUG_VECTOR_LENGTH,
        tmpPCur.y + rotatedNormal.y * DEBUG_VECTOR_LENGTH,
        tmpPCur.z + rotatedNormal.z * DEBUG_VECTOR_LENGTH
      );
    }

    const buildSegments = (positions, color, name) => {
      const geom = new THREE.BufferGeometry();
      geom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      const mat = new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.9, depthTest: false });
      const segs = new THREE.LineSegments(geom, mat);
      segs.name = name;
      segs.renderOrder = RENDER_ORDER_CURVE;
      segs.frustumCulled = false;
      return segs;
    };

    const tangentSegs = buildSegments(tangentLines, TANGENT_COLOR, 'TrimlineTangents');
    const normalSegs = buildSegments(normalLines, NORMAL_COLOR, 'TrimlineNormals');
    curveGroupRef.current.add(tangentSegs);
    curveGroupRef.current.add(normalSegs);
  };

  // Update visual elements
  const updateVisuals = () => {
    if (!scene) return;
    
    ensureGroups();
    
    // Clear existing visuals
    pointGroupRef.current.clear();
    curveGroupRef.current.clear();
    handlesGroupRef.current.clear();
    
    // Create point spheres
    trimlineState.points.forEach((point, index) => {
      const isSelected = trimlineState.selectedPointIndex === index;
      const sphere = createPointSphere(point, index, isSelected);
      pointGroupRef.current.add(sphere);
    });
    
    // Create bezier handles if a point is selected
    if (trimlineState.showBezierHandles && trimlineState.selectedPointIndex !== null) {
      const pointIndex = trimlineState.selectedPointIndex;
      const point = trimlineState.points[pointIndex];
      const handles = trimlineState.bezierHandles[pointIndex];
      
      if (point && handles) {
        const inPos = new THREE.Vector3(point.x + handles.in.x, point.y + handles.in.y, point.z + handles.in.z);
        const outPos = new THREE.Vector3(point.x + handles.out.x, point.y + handles.out.y, point.z + handles.out.z);
        
        const inHandle = createHandleSphere(inPos, pointIndex, 'in');
        const outHandle = createHandleSphere(outPos, pointIndex, 'out');
        
        handlesGroupRef.current.add(inHandle);
        handlesGroupRef.current.add(outHandle);
        
        // Add lines connecting handles to point
        const handleLineMaterial = new THREE.LineBasicMaterial({ color: HANDLE_LINE_COLOR, opacity: 0.8, transparent: true, linewidth: 2 });
        const renderOrder = 1001;

        const inLineGeom = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(point.x, point.y, point.z), inPos]);
        const inLine = new THREE.Line(inLineGeom, handleLineMaterial);
        inLine.renderOrder = renderOrder;
        handlesGroupRef.current.add(inLine);
        
        const outLineGeom = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(point.x, point.y, point.z), outPos]);
        const outLine = new THREE.Line(outLineGeom, handleLineMaterial);
        outLine.renderOrder = renderOrder;
        handlesGroupRef.current.add(outLine);
      }
    }
    
    // Create curve
    const curve = createCurve();
    curveGroupRef.current.add(curve);

    // Add debug vectors for tangent and 90° rotated normals
    // if (curve?.userData?.positions) {
    //   try {
    //     addCurveDebugVectors(curve.userData.positions, curve.userData.normals);
    //   } catch (e) {
    //     console.warn('Failed to add curve debug vectors:', e);
    //   }
    // }
  };

  // Initialize BVH for the mesh
  const initializeBVH = (mesh) => {
    if (!mesh || !mesh.geometry) return;
    
    try {
      // Enable BVH acceleration for the mesh
      THREE.BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
      THREE.BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;
      THREE.Mesh.prototype.raycast = acceleratedRaycast;

      // Always rebuild BVH to ensure it's up-to-date with current geometry
      // (geometry may have been transformed by alignment or other operations)
      if (mesh.geometry.boundsTree) {
        mesh.geometry.disposeBoundsTree();
      }
      mesh.geometry.computeBoundsTree();

      bvhRef.current = mesh.geometry.boundsTree;
    } catch (error) {
      bvhRef.current = null;
    }
  };

/**
 * Ensure preview mesh
 * @returns {THREE.Mesh} - The preview mesh
 */
  const ensurePreview = () => {
    if (previewMesh && previewMesh.parent) return previewMesh;
    if (previewMeshRef.current) return previewMeshRef.current;
    if (!scene || !shadedMesh) return null;

    // Store original mesh for undo
    // Note: We need to detect if the geometry itself has changed (e.g., after alignment transformation)
    // by checking if the geometry's UUID has changed or if the geometry reference differs
    const geometryChanged = baseGeomRef.current && (
      !shadedMesh.geometry.uuid ||
      shadedMesh.geometry.attributes.position !== baseGeomRef.current.attributes.position
    );

    if (!baseGeomRef.current || lastMeshRef.current !== shadedMesh || geometryChanged) {
      baseGeomRef.current = shadedMesh.geometry.clone();
      lastMeshRef.current = shadedMesh;
      originalMaterialRef.current = material;
      setOriginalMesh(shadedMesh.clone());

      // Initialize BVH for the mesh
      initializeBVH(shadedMesh);
    }

    const cloneGeom = baseGeomRef.current.clone();
    const mat = CreateElongationPreviewMaterial(0.5);
    // const mat = new THREE.MeshNormalMaterial({
    //   transparent: true,
    //   opacity: 0.8,
    //   side: THREE.DoubleSide,
    // });
    const clone = new THREE.Mesh(cloneGeom, mat);
    clone.name = 'TrimlinePreview';
    clone.matrixAutoUpdate = true;
    clone.position.copy(shadedMesh.position);
    clone.quaternion.copy(shadedMesh.quaternion);
    clone.scale.copy(shadedMesh.scale);
    scene.add(clone);

    previewMeshRef.current = clone;
    setPreviewMesh(clone);
    return clone;
  };

  /**
   * Dispose preview
   * @param {Object} options - The options for disposing the preview
   * @param {boolean} options.keepIfReplaced - Whether to keep the preview if it is replaced
   */
  const disposePreview = (options = { keepIfReplaced: false }) => {
    const p = previewMeshRef.current;
    if (!p) return;
    if (options.keepIfReplaced) {
      setPreviewMesh(null);
      return;
    }
    if (p.parent) p.parent.remove(p);
    p.geometry?.dispose?.();
    p.material?.dispose?.();
    setPreviewMesh(null);
    previewMeshRef.current = null;
  };

  /**
   * Generate a hash key from curve state (points + handles) for cache invalidation
   * @returns {string} - Hash string representing the current curve configuration
   */
  const getCurveStateHash = () => {
    // Read from store directly to avoid stale closures
    const currentState = useTrimlineStore.getState().trimline;
    const points = currentState.points || [];
    const handles = currentState.bezierHandles || [];
    return JSON.stringify({ points, handles });
  };

  /**
   * Check if recomputation is required based on curve changes
   * @returns {boolean} - True if preview mesh needs to be recomputed
   */
  const isRecomputationRequired = () => {
    // Read from store directly to avoid stale closures
    const currentState = useTrimlineStore.getState();
    const currentHash = getCurveStateHash();
    const previewHash = previewCurveStateHashRef.current;
    
    // If no preview exists, recomputation is required
    if (!currentState.previewMesh && !previewMeshRef.current) {
      return true;
    }
    
    // If hash changed, recomputation is required
    if (previewHash !== currentHash) {
      return true;
    }
    
    return false;
  };

  /**
   * Rebuild preview with cut mesh (ASYNC with worker support)
   * @param {boolean} forceCompute - If true, compute regardless of preview visibility
   */
  const rebuildPreviewAsync = async (forceCompute = false) => {
    // Cancel previous operation to prevent race conditions
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller and operation ID for this operation
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    // Increment operation ID for this new operation
    operationIdRef.current += 1;
    const currentOperationId = operationIdRef.current;

    // Check if already cancelled before starting
    if (signal.aborted) return;

    try {
      const p = ensurePreview();
      if (!p || !shadedMesh) return;

      // CRITICAL: Allow computation when forced (e.g., from Apply button) or when preview is visible
      if (!forceCompute && !trimlineState?.previewVisible) return;

      // Set worker loading state at the start
      const { setWorkerLoading } = useEditorStore.getState();
      console.log(`[TrimlineHandler] Operation ${currentOperationId}: Setting worker loading state to TRUE`);
      setWorkerLoading(true, 'Computing trimline cut');

      if (baseGeomRef.current) {
        // Read from store directly to get latest state when called externally
        const currentTrimlineState = useTrimlineStore.getState().trimline;
        // Build curve positions at very high resolution and project to surface for precision
        const curvePositions = generateCurvePoints(
          currentTrimlineState.points,
          currentTrimlineState.bezierHandles,
          100 // Much higher resolution for cutting precision
        );

        // Check if cancelled after curve generation
        if (signal.aborted) {
          console.log(`[TrimlineHandler] Operation ${currentOperationId}: Cancelled after curve generation`);
          setWorkerLoading(false);
          return;
        }

        // Use the same enhanced projection pipeline as the visual curve
        let worldPositions = curvePositions;
        if (shadedMesh && bvhRef.current) {
          worldPositions = projectCurvePointsOntoMesh(curvePositions, shadedMesh, bvhRef.current);

          // Apply the same surface normal offset for cutting precision
          const offsetAmount = 0.01; // Smaller offset for cutting to maintain accuracy
          for (let i = 0; i < worldPositions.length; i += 3) {
            const point = new THREE.Vector3(worldPositions[i], worldPositions[i + 1], worldPositions[i + 2]);
            const normal = getSurfaceNormalAtPoint(
              { x: point.x, y: point.y, z: point.z },
              shadedMesh,
              bvhRef.current
            );

            // Slight offset along surface normal for clean cutting
            worldPositions[i] += normal.x * offsetAmount;
            worldPositions[i + 1] += normal.y * offsetAmount;
            worldPositions[i + 2] += normal.z * offsetAmount;
          }
        }

        // Check if cancelled after projection
        if (signal.aborted) {
          console.log(`[TrimlineHandler] Operation ${currentOperationId}: Cancelled after projection`);
          setWorkerLoading(false);
          return;
        }

        // Perform CSG subtraction to keep bottom part
        const vectorPositions = convertToWorldPositions(worldPositions);

        // Compute curve normals for these samples and pass to cutter
        let curveNormalsVec3 = null;
        try {
          const flatNormals = computeCurveNormalsFlat(worldPositions);
          if (flatNormals && flatNormals.length === worldPositions.length) {
            curveNormalsVec3 = [];
            for (let i = 0; i < flatNormals.length; i += 3) {
              curveNormalsVec3.push(new THREE.Vector3(flatNormals[i], flatNormals[i + 1], flatNormals[i + 2]));
            }
          }
        } catch (e) {
          console.warn('[TrimlineHandler] Failed to compute curve normals:', e);
        }

        // Prepare mesh data for worker
        const meshData = prepareMeshDataForWorker(shadedMesh);

        // Convert curve data to serializable format
        const curvePointsData = vectorPositions.map(v => ({ x: v.x, y: v.y, z: v.z }));
        const curveNormalsData = curveNormalsVec3
          ? curveNormalsVec3.map(n => ({ x: n.x, y: n.y, z: n.z }))
          : null;

        // Check if cancelled before worker call
        if (signal.aborted) {
          console.log(`[TrimlineHandler] Operation ${currentOperationId}: Cancelled before worker call`);
          setWorkerLoading(false);
          return;
        }

        // Perform cutting in worker thread
        console.log(`[TrimlineHandler] Operation ${currentOperationId}: Starting worker operation`);
        const workerResult = await cutMeshByLoopAsync(
          {
            positions: meshData.positions,
            indices: meshData.indices,
            colors: meshData.colors,
            curvePoints: curvePointsData,
            curveNormals: curveNormalsData,
            keepBothParts: true, // Get both parts for upper mesh
            matrixWorld: meshData.matrixWorld
          },
          signal
        );

        // Check if cancelled after worker completes
        if (signal.aborted) {
          console.log(`[TrimlineHandler] Operation ${currentOperationId}: Cancelled after worker completion`);
          setWorkerLoading(false);
          return;
        }

        console.log(`[TrimlineHandler] Operation ${currentOperationId}: Worker completed successfully`);

        // Reconstruct geometry from worker result
        const newGeom = reconstructGeometryFromWorkerResult(workerResult.lower);

        if (newGeom) {
          // Handle upper mesh if available
          if (workerResult.upper) {
            try {
              const upperGeom = reconstructGeometryFromWorkerResult(workerResult.upper);
              if (upperGeom) {
                const upperMaterial = material.clone();
                const upperMesh = new THREE.Mesh(upperGeom, upperMaterial);
                upperMesh.position.copy(shadedMesh.position);
                upperMesh.quaternion.copy(shadedMesh.quaternion);
                upperMesh.scale.copy(shadedMesh.scale);
                upperMesh.visible = false; // Keep it invisible
                upperMesh.name = 'TrimlineUpperMesh';
                setUpperMesh(upperMesh);
              }
            } catch (error) {
              console.warn('[TrimlineHandler] Failed to create upper mesh:', error);
            }
          }

          // Update preview mesh geometry
          if (p.geometry) p.geometry.dispose();
          p.geometry = newGeom;

          try {
            p.geometry.computeVertexNormals();
            p.geometry.computeBoundingSphere();
            p.geometry.computeBoundingBox();
          } catch (computeError) {
            console.warn('[TrimlineHandler] Failed to compute geometry attributes:', computeError);
          }
          
          // Update curve state hash after successful computation
          previewCurveStateHashRef.current = getCurveStateHash();
        }

        console.log(`[TrimlineHandler] Operation ${currentOperationId}: Preview updated successfully`);
      }

      // Clear loading state on success
      const { setWorkerLoading: setWorkerLoadingFinal } = useEditorStore.getState();
      console.log(`[TrimlineHandler] Operation ${currentOperationId}: Setting worker loading state to FALSE (success)`);
      setWorkerLoadingFinal(false);

    } catch (error) {
      // Check if error is due to cancellation
      if (error.name === 'AbortError' || error.message?.includes('cancelled')) {
        console.log(`[TrimlineHandler] Operation ${currentOperationId}: Operation cancelled`);
      } else {
        console.error('[TrimlineHandler] Failed to rebuild preview:', error);
      }

      // Clear loading state on error
      const { setWorkerLoading } = useEditorStore.getState();
      console.log(`[TrimlineHandler] Operation ${currentOperationId}: Setting worker loading state to FALSE (error)`);
      setWorkerLoading(false);
    }
  };

  /**
   * Immediate (non-debounced) preview rebuild for apply operations
   * Bypasses all checks and forces immediate computation
   */
  const rebuildPreviewImmediate = async () => {
    console.log('[TrimlineHandler] Forcing immediate preview rebuild for apply');
    await rebuildPreviewAsync(true);
  };

  // Mouse event handlers with error handling
  const handleMouseDown = (event) => {
    try {
      if (event.altKey) {
        if (!shadedMesh || !bvhRef.current || !renderer || !camera || !scene) return;

        const rect = renderer.domElement.getBoundingClientRect();
        mouse.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        mouse.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        raycaster.current.setFromCamera(mouse.current, camera);

        // Transform ray to local space for BVH raycast
        const worldToLocal = new THREE.Matrix4().copy(shadedMesh.matrixWorld).invert();
        const localToWorld = shadedMesh.matrixWorld;

        const localRay = new THREE.Ray();
        localRay.origin.copy(raycaster.current.ray.origin).applyMatrix4(worldToLocal);
        localRay.direction.copy(raycaster.current.ray.direction).transformDirection(worldToLocal).normalize();

        const hit = bvhRef.current.raycastFirst(localRay);
        if (hit) {
          // Transform hit point to world space
          const hitPoint = new THREE.Vector3().copy(hit.point).applyMatrix4(localToWorld);
          
          const curve = curveGroupRef.current?.getObjectByName('TrimlineCurve');
          if (curve && curve.userData.positions) {
            const flatPositions = curve.userData.positions;
            const flatNormals = curve.userData.normals;
            const trimlinePoints = [];
            const trimlineNormals = [];
            for (let i = 0; i < flatPositions.length; i += 3) {
              trimlinePoints.push(new THREE.Vector3(flatPositions[i], flatPositions[i+1], flatPositions[i+2]));
              if (flatNormals && flatNormals.length === flatPositions.length) {
                trimlineNormals.push(new THREE.Vector3(flatNormals[i], flatNormals[i+1], flatNormals[i+2]));
              }
            }

            if (trimlinePoints.length > 1) {
              const isBelow = isPointBelowTrimline(hitPoint, trimlinePoints, trimlineNormals.length === trimlinePoints.length ? trimlineNormals : null);
              
              
              // Visualize the point
              const debugSphere = new THREE.Mesh(
                new THREE.SphereGeometry(0.1, 16, 12),
                new THREE.MeshBasicMaterial({ color: isBelow ? 0x00ff00 : 0xff0000, depthTest: false }) // green if below, red if above
              );
              debugSphere.position.copy(hitPoint);
              scene.add(debugSphere);
              setTimeout(() => {
                if(debugSphere.parent) {
                  debugSphere.parent.remove(debugSphere);
                  debugSphere.geometry.dispose();
                  debugSphere.material.dispose();
                }
              }, 2000);
            }
          }
        }
        event.preventDefault();
        event.stopPropagation();
        return; // Prevent normal drag/select behavior
      }

      if (!camera || !scene || !renderer) {
        return;
      }
      
      const rect = renderer.domElement?.getBoundingClientRect();
      if (!rect) {
        return;
      }
      
      mouse.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      
      // Validate mouse coordinates
      if (!isFinite(mouse.current.x) || !isFinite(mouse.current.y)) {
        return;
      }
      
      raycaster.current.setFromCamera(mouse.current, camera);
      
      const pointObjects = pointGroupRef.current ? pointGroupRef.current.children : [];
      const handleObjects = handlesGroupRef.current ? handlesGroupRef.current.children.filter(child => child.userData?.isHandle) : [];
      const allObjects = [...pointObjects, ...handleObjects];
      
      if (allObjects.length === 0) {
        selectPoint(null);
        return;
      }
      
      const intersects = raycaster.current.intersectObjects(allObjects);
      
      if (intersects.length > 0) {
        const obj = intersects[0].object;
        const userData = obj.userData || {};
        
        if (userData.isControlPoint && typeof userData.pointIndex === 'number') {
          selectPoint(userData.pointIndex);
          isDragging.current = true;
          draggingPointRef.current = true;
          dragStart.current.copy(intersects[0].point);
          if (controls) controls.enabled = false;
        } else if (userData.isHandle) {
          draggingHandleRef.current = obj;
          draggingPointRef.current = false;
          // Ensure the owning point is selected and prepare drag plane
          const latest = useTrimlineStore.getState().trimline;
          const anchorPoint = latest.points[userData.pointIndex];
          if (anchorPoint) {
            selectPoint(userData.pointIndex);
            handleAnchorRef.current.set(anchorPoint.x, anchorPoint.y, anchorPoint.z);
            
            // Use the surface normal at this point to define the drag plane
            const surfaceNormal = pointNormalsRef.current.get(userData.pointIndex);
            if (surfaceNormal) {
              // Create a drag plane perpendicular to the surface normal
              // This constrains handle movement to the tangent plane of the surface
              handleDragPlaneRef.current.setFromNormalAndCoplanarPoint(surfaceNormal, handleAnchorRef.current);
            } else {
              // Fallback to camera-facing plane
              camera.getWorldDirection(cameraDirRef.current);
              cameraDirRef.current.normalize();
              handleDragPlaneRef.current.setFromNormalAndCoplanarPoint(cameraDirRef.current, handleAnchorRef.current);
            }
          }
          isDragging.current = true;
          dragStart.current.copy(intersects[0].point);
          if (controls) controls.enabled = false;
        }
      } else {
        selectPoint(null);
      }
    } catch (error) {
      // Reset dragging state on error
      isDragging.current = false;
      if (controls) controls.enabled = true;
    }
  };

  const handleMouseMove = (event) => {
    try {
      if (!isDragging.current || !camera || !renderer) {
        // console.log('Mouse move conditions not met:', { 
        //   isDragging: isDragging.current, 
        //   camera: !!camera, 
        //   renderer: !!renderer 
        // });
        return;
      }
      
      const rect = renderer.domElement?.getBoundingClientRect();
      if (!rect) return;
      
      mouse.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      
      // Validate mouse coordinates
      if (!isFinite(mouse.current.x) || !isFinite(mouse.current.y)) return;
      
      raycaster.current.setFromCamera(mouse.current, camera);
      
      const latest = useTrimlineStore.getState().trimline;
      if (draggingHandleRef.current) {
        const handle = draggingHandleRef.current;
        const userData = handle.userData || {};
        const pointIndex = userData.pointIndex;
        const handleType = userData.handleType;

        if (pointIndex !== null && handleType !== null) {
          const latestState = useTrimlineStore.getState().trimline;
          const point = latestState.points[pointIndex];
          const handles = latestState.bezierHandles[pointIndex];

          if (point && handles) {
            // Method 1: Try intersecting with the surface normal plane
            const intersection = tmpIntersectionRef.current;
            let worldIntersection = null;
            
            if (raycaster.current.ray.intersectPlane(handleDragPlaneRef.current, intersection)) {
              worldIntersection = intersection.clone();
            } else {
              // Robust fallback: compute ray-plane intersection manually; if parallel, project origin onto plane
              const plane = handleDragPlaneRef.current;
              const ray = raycaster.current.ray;
              const denom = plane.normal.dot(ray.direction);
              if (Math.abs(denom) > 1e-6) {
                const t = -plane.distanceToPoint(ray.origin) / denom;
                worldIntersection = ray.origin.clone().add(ray.direction.clone().multiplyScalar(t));
              } else {
                // Ray is parallel to the plane; project the ray origin onto the plane
                worldIntersection = plane.projectPoint(ray.origin, new THREE.Vector3());
              }
            }
            
            if (worldIntersection) {
              // Calculate the relative position from the anchor point
              let relativePos = {
                x: worldIntersection.x - point.x,
                y: worldIntersection.y - point.y,
                z: worldIntersection.z - point.z
              };
              
              // Ensure the handle stays in the surface normal plane
              const surfaceNormal = pointNormalsRef.current.get(pointIndex);
              if (surfaceNormal) {
                const relativePosVec = new THREE.Vector3(relativePos.x, relativePos.y, relativePos.z);
                // Remove any component along the surface normal
                const normalComponent = relativePosVec.dot(surfaceNormal);
                relativePosVec.sub(surfaceNormal.clone().multiplyScalar(normalComponent));
                
                relativePos = {
                  x: relativePosVec.x,
                  y: relativePosVec.y,
                  z: relativePosVec.z
                };
              }
              
              // For aligned handles, when one handle moves, the opposite moves in the opposite direction
              // but preserves its existing length
              if (handleType === 'in') {
                updateBezierHandle(pointIndex, 'in', relativePos);
                
                // Mirror the direction for tangency but preserve the out handle's length
                const currentOutHandle = handles.out;
                const currentOutLength = Math.sqrt(currentOutHandle.x * currentOutHandle.x + currentOutHandle.y * currentOutHandle.y + currentOutHandle.z * currentOutHandle.z);
                const newInLength = Math.sqrt(relativePos.x * relativePos.x + relativePos.y * relativePos.y + relativePos.z * relativePos.z);
                
                if (newInLength > 0) {
                  const normalizedDirection = {
                    x: -relativePos.x / newInLength,
                    y: -relativePos.y / newInLength,
                    z: -relativePos.z / newInLength
                  };
                  
                  updateBezierHandle(pointIndex, 'out', {
                    x: normalizedDirection.x * currentOutLength,
                    y: normalizedDirection.y * currentOutLength,
                    z: normalizedDirection.z * currentOutLength
                  });
                }
              } else if (handleType === 'out') {
                updateBezierHandle(pointIndex, 'out', relativePos);
                
                // Mirror the direction for tangency but preserve the in handle's length
                const currentInHandle = handles.in;
                const currentInLength = Math.sqrt(currentInHandle.x * currentInHandle.x + currentInHandle.y * currentInHandle.y + currentInHandle.z * currentInHandle.z);
                const newOutLength = Math.sqrt(relativePos.x * relativePos.x + relativePos.y * relativePos.y + relativePos.z * relativePos.z);
                
                if (newOutLength > 0) {
                  const normalizedDirection = {
                    x: -relativePos.x / newOutLength,
                    y: -relativePos.y / newOutLength,
                    z: -relativePos.z / newOutLength
                  };
                  
                  updateBezierHandle(pointIndex, 'in', {
                    x: normalizedDirection.x * currentInLength,
                    y: normalizedDirection.y * currentInLength,
                    z: normalizedDirection.z * currentInLength
                  });
                }
              }
            }
            return;
          }
        }
      } else if (latest.selectedPointIndex !== null && draggingPointRef.current) {
        // Point dragging branch
        // Try to intersect with the mesh first for surface constraint using BVH
        if (shadedMesh && bvhRef.current) {
          // Transform ray to local space for BVH raycast
          const worldToLocal = new THREE.Matrix4().copy(shadedMesh.matrixWorld).invert();
          const localToWorld = shadedMesh.matrixWorld;

          const localRay = new THREE.Ray();
          localRay.origin.copy(raycaster.current.ray.origin).applyMatrix4(worldToLocal);
          localRay.direction.copy(raycaster.current.ray.direction).transformDirection(worldToLocal).normalize();

          const meshIntersects = bvhRef.current.raycastFirst(localRay);
          if (meshIntersects) {
            // Transform hit point back to world space
            const worldPoint = new THREE.Vector3().copy(meshIntersects.point).applyMatrix4(localToWorld);
            const newPoint = {
              x: worldPoint.x,
              y: worldPoint.y,
              z: worldPoint.z
            };
            const idx = latest.selectedPointIndex;
            const prevHandles = latest.bezierHandles?.[idx];
            let prevNormal = pointNormalsRef.current.get(idx);
            if (!prevNormal || prevNormal.length() < 1e-6) {
              // Fallback: estimate previous normal from current stored point
              const prevPoint = latest.points[idx];
              if (prevPoint) prevNormal = getSurfaceNormalAtPoint(prevPoint, shadedMesh, bvhRef.current);
            }

            updatePoint(idx, newPoint);

            // Compute and cache new normal at updated point
            const newNormal = getSurfaceNormalAtPoint(newPoint, shadedMesh, bvhRef.current).clone().normalize();
            pointNormalsRef.current.set(idx, newNormal);

            // Preserve handle lengths and rotate handles from old normal to new normal
            if (prevHandles) {
              const inVecPrev = new THREE.Vector3(prevHandles.in.x, prevHandles.in.y, prevHandles.in.z);
              const outVecPrev = new THREE.Vector3(prevHandles.out.x, prevHandles.out.y, prevHandles.out.z);

              const inLen = inVecPrev.length();
              const outLen = outVecPrev.length();

              let rotatedIn = inVecPrev.clone();
              let rotatedOut = outVecPrev.clone();

              if (prevNormal && prevNormal.length() > 1e-6) {
                const q = new THREE.Quaternion().setFromUnitVectors(prevNormal.clone().normalize(), newNormal);
                rotatedIn.applyQuaternion(q);
                rotatedOut.applyQuaternion(q);
              }

              // Project into new tangent plane and re-scale to preserve lengths
              const projectToPlaneAndScale = (v, normal, targetLen) => {
                const projected = v.clone().sub(normal.clone().multiplyScalar(v.dot(normal)));
                const len = projected.length();
                if (len < 1e-6) return projected.set(1, 0, 0).sub(normal.clone().multiplyScalar(new THREE.Vector3(1, 0, 0).dot(normal))).normalize().multiplyScalar(targetLen);
                return projected.multiplyScalar(targetLen / len);
              };

              const inVecNew = projectToPlaneAndScale(rotatedIn, newNormal, inLen);
              const outVecNew = projectToPlaneAndScale(rotatedOut, newNormal, outLen);

              const { updateBezierHandle } = useTrimlineStore.getState();
              updateBezierHandle(idx, 'in', { x: inVecNew.x, y: inVecNew.y, z: inVecNew.z });
              updateBezierHandle(idx, 'out', { x: outVecNew.x, y: outVecNew.y, z: outVecNew.z });
            } else {
              // Fallback if no previous handles: initialize once in the new plane
              const newHandles = initializeHandlesInNormalPlane(idx);
              if (newHandles) {
                const { updateBezierHandle } = useTrimlineStore.getState();
                updateBezierHandle(idx, 'in', newHandles.in);
                updateBezierHandle(idx, 'out', newHandles.out);
              }
            }
            
            return;
          }
        }

        // Fallback: intersect with horizontal plane at the original point height
        const originalPoint = latest.points[latest.selectedPointIndex];
        if (originalPoint) {
          const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -originalPoint.y);
          const intersection = new THREE.Vector3();
          if (raycaster.current.ray.intersectPlane(plane, intersection)) {

            updatePoint(latest.selectedPointIndex, {
              x: intersection.x,
              y: originalPoint.y, // Keep Y constant for horizontal movement
              z: intersection.z
            });
          }
        }
      }
    } catch (error) {
    }
  };

  const handleMouseUp = () => {
    isDragging.current = false;
    draggingHandleRef.current = null; // Clear handle dragging state
    draggingPointRef.current = false;
    if (controls) controls.enabled = true;
  };

  // Effects
  useEffect(() => {
    return () => {
      const latest = useTrimlineStore.getState();

      disposePreview();
      setPreviewMesh(null);
    };
  }, []);

  // Reset base references when unapplied (but not when applied from draft)
  useEffect(() => {
    if (!isTrimlineApplied && !isAppliedFromDraft && scene && shadedMesh) {
      baseGeomRef.current = null;
      lastMeshRef.current = null;
      originalMaterialRef.current = null;
      bvhRef.current = null; // Reset BVH reference
      debugGroupRef.current = null; // Reset debug group reference
      pointNormalsRef.current.clear(); // Clear cached normals
      previewCurveStateHashRef.current = null; // Reset preview hash

      if (previewMeshRef.current) {
        disposePreview();
      }
    }
  }, [isTrimlineApplied, isAppliedFromDraft, scene, shadedMesh]);

  // Setup on mount
  useEffect(() => {
    if (!scene || !shadedMesh) return;
    
    // Ensure preview and BVH are ready before initializing points
    ensurePreview();

    // Calculate mesh bounds for initialization check
    if (!shadedMesh.geometry.boundingBox) shadedMesh.geometry.computeBoundingBox();
    const bbox = shadedMesh.geometry.boundingBox.clone();
    bbox.applyMatrix4(shadedMesh.matrixWorld);
    
    // Initialize points if they're still defaults or haven't been projected onto surface
    // But skip initialization if points were restored from draft
    const needsInitialization = !isAppliedFromDraft && trimlineState.points.some(point =>
      (Math.abs(point.x) < 0.1 && Math.abs(point.y) < 0.1 && Math.abs(point.z - 5) < 0.1) ||
      (point.y > bbox.max.y + 10 || point.y < bbox.min.y - 10) // Points are way off the mesh
    );

    if (needsInitialization) {
      if (bvhRef.current) {
        const newPoints = initializeDefaultPoints(shadedMesh, bvhRef.current, scene);//, debugGroupRef);
        
        // Initialize handles based on surface normals
        const newHandles = newPoints.map((_, index) => {
          return initializeHandlesInNormalPlane(index, 2.0, newPoints, true) || {
            in: { x: -1, y: 0, z: 0 },
            out: { x: 1, y: 0, z: 0 }
          };
        });
        
        // Store these as initial points for future resets
        setInitialPoints([...newPoints], [...newHandles]);
        
        const { setTrimline } = useTrimlineStore.getState();
        setTrimline({ 
          points: newPoints,
          bezierHandles: newHandles 
        });
      } else {
      }
    }
    
    updateVisuals();
    rebuildPreviewAsync();

    // Add mouse event listeners
    const canvas = renderer?.domElement;
    if (canvas) {
      canvas.addEventListener('mousedown', handleMouseDown, false);
      canvas.addEventListener('mousemove', handleMouseMove, false);
      canvas.addEventListener('mouseup', handleMouseUp, false);
      
      // Also add to window for mouse up to catch events outside canvas
      window.addEventListener('mouseup', handleMouseUp, false);
    }
    
    return () => {
      if (canvas) {
        canvas.removeEventListener('mousedown', handleMouseDown);
        canvas.removeEventListener('mousemove', handleMouseMove);
        canvas.removeEventListener('mouseup', handleMouseUp);
      }
      window.removeEventListener('mouseup', handleMouseUp);
      disposeGroups();
    };
  }, [scene, shadedMesh]);

  // Update visuals when trimline state changes (points/handles/selection)
  // Note: intentionally exclude previewVisible to avoid double rebuilds when toggling preview ON
  useEffect(() => {
    if (!scene) return;
    
    // Invalidate preview hash when points or handles change
    // This ensures recomputation is triggered on next apply
    const currentHash = getCurveStateHash();
    if (previewCurveStateHashRef.current !== currentHash) {
      previewCurveStateHashRef.current = null; // Invalidate hash
    }
    
    updateVisuals();
    if (trimlineState?.previewVisible) {
      rebuildPreviewAsync();
    }
  }, [trimlineState.points, trimlineState.selectedPointIndex, trimlineState.showBezierHandles, trimlineState.bezierHandles]);

  // Handle mesh changes
  useEffect(() => {
    if (lastMeshRef.current !== shadedMesh && scene && shadedMesh) {
      // CRITICAL: Don't trigger automatic preview rebuild if trimline is already applied
      // (the mesh change is from the apply operation replacing the mesh)
      // This prevents aborting the current apply operation
      if (!isTrimlineApplied) {
        baseGeomRef.current = null;
        bvhRef.current = null; // Reset BVH when mesh changes
        pointNormalsRef.current.clear(); // Clear cached normals
        ensurePreview();
        rebuildPreviewAsync();
      }
    }
  }, [shadedMesh, isTrimlineApplied]);

  // Control visibility based on enabled state
  useEffect(() => {
    const enabled = !!trimlineState?.enabled;
    if (!scene) return;
    
    [pointGroupRef.current, curveGroupRef.current, handlesGroupRef.current].forEach(group => {
      if (group) group.visible = enabled;
    });
  }, [trimlineState?.enabled]);

  // Preview visibility and single rebuild when toggled ON
  useEffect(() => {
    const visible = !!trimlineState?.enabled && !!trimlineState?.previewVisible;

    // Handle visibility update if preview mesh exists
    if (previewMesh) {
      const wasVisible = previewMesh.visible === true;
      previewMesh.visible = visible;
      // Rebuild only on rising edge (OFF -> ON) when previewMesh exists
      if (!wasVisible && visible) {
        rebuildPreviewAsync(false); // Normal preview visibility toggle
      }
    } else if (visible) {
      // CRITICAL: If preview should be visible but previewMesh doesn't exist yet,
      // we still need to compute the preview. This happens when user clicks Apply
      // without ever enabling preview visibility.
      // Pass forceCompute=true to ensure computation happens regardless of any checks
      rebuildPreviewAsync(true);
    }
  }, [trimlineState?.enabled, trimlineState?.previewVisible, previewMesh]);

  // Adjust preview mesh material based on ghost mode
  useEffect(() => {
    const mesh = previewMesh || previewMeshRef.current;

    // Only update if preview mesh exists
    if (!mesh || !mesh.material) return;

    if (ghostModeEnabled) {
      // Ghost mode ON: Make preview completely opaque
      mesh.material.opacity = 1.0;
      mesh.material.transparent = false;
    } else {
      // Ghost mode OFF: Make preview transparent
      mesh.material.opacity = 0.5;
      mesh.material.transparent = true;
    }

    mesh.material.needsUpdate = true;
  }, [ghostModeEnabled, previewMesh]);

  // Handle re-initialization request
  useEffect(() => {
    if (shouldReinitialize && scene && shadedMesh && bvhRef.current) {
      console.log('[TrimlineHandler] Re-initializing points...');

      // Initialize points from mesh surface
      const newPoints = initializeDefaultPoints(shadedMesh, bvhRef.current, scene);

      // Initialize handles based on surface normals
      const newHandles = newPoints.map((_, index) => {
        return initializeHandlesInNormalPlane(index, 2.0, newPoints, true) || {
          in: { x: -1, y: 0, z: 0 },
          out: { x: 1, y: 0, z: 0 }
        };
      });

      // Store these as initial points for future resets
      setInitialPoints([...newPoints], [...newHandles]);

      // Update trimline state
      const { setTrimline } = useTrimlineStore.getState();
      setTrimline({
        points: newPoints,
        bezierHandles: newHandles,
        selectedPointIndex: null
      });

      // Clear the flag
      clearReinitializeFlag();

      // Invalidate preview hash since points changed
      previewCurveStateHashRef.current = null;

      console.log('[TrimlineHandler] Points re-initialized successfully');
    }
  }, [shouldReinitialize, scene, shadedMesh]);

  // Expose rebuild functions to store for apply operations
  useEffect(() => {
    const { setTrimline } = useTrimlineStore.getState();
    // Store the functions in a way that can be accessed from the store
    // We'll use a different approach - expose via a ref or store method
    // For now, we'll expose via the store's setTrimline with a special flag
    // Actually, let's expose it properly via a store method
  }, []);

  // Expose rebuildPreviewAsync and isRecomputationRequired to store
  // Functions are stable references, so no dependencies needed
  useEffect(() => {
    useTrimlineStore.setState({
      rebuildPreviewAsync: rebuildPreviewAsync,
      rebuildPreviewImmediate: rebuildPreviewImmediate,
      isRecomputationRequired: isRecomputationRequired
    });
  }, []); // Empty deps - functions are stable references

  return null;
};

export default TrimlineHandler;